// G:\qkard\frontend\src\pages\DesignsTab.jsx
// Phase 1E — Designs tab for lookbook verticals (furniture, tailor, tiles, jewellery...)
//
// Props:
//   business   — business object from OwnerDashboard
//   catalogue  — catalogue object
//   items      — current items array
//   onRefresh  — callback to reload items after add/edit/delete

import { useState, useRef } from "react";

const API = "https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod";
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const CATEGORY_OPTIONS = {
  furniture: ["Sofa", "Bed", "Wardrobe", "Dining", "Study", "Storage", "Outdoor"],
  tailor:    ["Salwar", "Lehenga", "Suit", "Saree", "Blouse", "Kurti", "Sherwani"],
  tiles:     ["Floor", "Wall", "Marble", "Outdoor", "Bathroom", "Kitchen"],
  jewellery: ["Ring", "Necklace", "Earring", "Bracelet", "Pendant", "Bangle"],
  tent:      ["Basic", "Premium", "Royal", "Corporate"],
  default:   ["Category 1", "Category 2", "Category 3"],
};
function getCategoryOptions(cat) {
  return CATEGORY_OPTIONS[(cat||"").toLowerCase()] || CATEGORY_OPTIONS.default;
}

const SPEC_FIELDS = {
  furniture: [
    { key: "dimensions", label: "Dimensions", placeholder: "e.g. 220 × 85 × 95 cm" },
    { key: "material",   label: "Material",   placeholder: "e.g. Sheesham wood" },
    { key: "leadTime",   label: "Lead Time",  placeholder: "e.g. 7-10 days" },
  ],
  tailor: [
    { key: "leadTime",   label: "Lead Time",  placeholder: "e.g. 5 days" },
  ],
  tiles: [
    { key: "dimensions", label: "Size",       placeholder: "e.g. 60×60 cm" },
    { key: "material",   label: "Material",   placeholder: "e.g. Porcelain" },
    { key: "finish",     label: "Finish",     placeholder: "e.g. Glossy" },
    { key: "minOrder",   label: "Min Order",  placeholder: "e.g. 10 sqft" },
  ],
  jewellery: [
    { key: "metal",  label: "Metal",  placeholder: "e.g. Gold 22K" },
    { key: "stone",  label: "Stone",  placeholder: "e.g. Diamond" },
    { key: "weight", label: "Weight", placeholder: "e.g. 12g" },
  ],
  default: [
    { key: "dimensions", label: "Size/Dimensions", placeholder: "" },
    { key: "material",   label: "Material",        placeholder: "" },
    { key: "leadTime",   label: "Lead Time",       placeholder: "" },
  ],
};
function getSpecFields(cat) {
  return SPEC_FIELDS[(cat||"").toLowerCase()] || SPEC_FIELDS.default;
}

async function uploadPhoto(file, business, itemId, photoIndex) {
  const phone = localStorage.getItem("qkard_phone");
  const presignRes = await fetch(`${API}/upload/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      phone,
      businessId: business.businessId,
      productId:  "qkard",
      fileType:   file.type,
      fileExt:    file.name.split(".").pop(),
      itemId:     `${itemId}-${photoIndex}-${Date.now()}`,
    }),
  });
  const { presignedUrl, publicUrl } = await presignRes.json();
  if (!presignedUrl) throw new Error("Could not get upload URL");
  const uploadRes = await fetch(presignedUrl, {
    method: "PUT", body: file,
    headers: { "Content-Type": file.type },
  });
  if (!uploadRes.ok) throw new Error("Upload failed");
  return publicUrl;
}

const emptyForm = () => ({
  name: "", category: "", description: "", price: "",
  photos: [], variants: [], specs: {}, tags: "",
});

export default function DesignsTab({ business, catalogue, items, onRefresh }) {
  const [showForm, setShowForm]   = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState(emptyForm());
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [progress, setProgress]   = useState("");
  const [filterCat, setFilterCat] = useState("All");
  const photoInputRef = useRef(null);

  const designItems = items.filter(i => i.isActive !== false);
  const catOptions  = getCategoryOptions(business?.category);
  const specFields  = getSpecFields(business?.category);
  const allCats     = ["All", ...Array.from(new Set(designItems.map(i => i.category).filter(Boolean)))];
  const filtered    = filterCat === "All" ? designItems : designItems.filter(i => i.category === filterCat);

  const openAdd = () => { setEditItem(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      name:        item.name || "",
      category:    item.category || "",
      description: item.description || "",
      price:       item.price?.toString() || "",
      photos:      (item.photos?.length ? item.photos : [item.photoUrl].filter(Boolean))
                    .map(url => ({ url, preview: url, file: null })),
      variants:    item.variants || [],
      specs:       item.specs || {},
      tags:        (item.tags || []).join(", "),
    });
    setShowForm(true);
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const toAdd = files.slice(0, 4 - form.photos.length)
      .map(file => ({ file, preview: URL.createObjectURL(file), url: null }));
    setForm(f => ({ ...f, photos: [...f.photos, ...toAdd] }));
    e.target.value = "";
  };
  const removePhoto   = (i) => setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));
  const addVariant    = () => setForm(f => ({ ...f, variants: [...f.variants, { label: "", price: "" }] }));
  const updateVariant = (i, key, val) => setForm(f => {
    const v = [...f.variants]; v[i] = { ...v[i], [key]: val }; return { ...f, variants: v };
  });
  const removeVariant = (i) => setForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  const updateSpec    = (key, val) => setForm(f => ({ ...f, specs: { ...f.specs, [key]: val } }));

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Design name is required"); return; }
    if (!form.price || isNaN(Number(form.price))) { alert("Valid price is required"); return; }
    setSaving(true);
    try {
      const itemId = editItem?.itemId || `design-${Date.now()}`;
      const photoUrls = [];
      const newPhotos = form.photos.filter(p => p.file);
      for (let i = 0; i < form.photos.length; i++) {
        const p = form.photos[i];
        if (p.file) {
          setProgress(`Uploading photo ${photoUrls.filter((_, j) => form.photos[j]?.file).length + 1} of ${newPhotos.length}...`);
          photoUrls.push(await uploadPhoto(p.file, business, itemId, i));
        } else {
          photoUrls.push(p.url);
        }
      }
      setProgress("");

      const variants = form.variants.filter(v => v.label.trim())
        .map(v => ({ label: v.label.trim(), price: Number(v.price) || Number(form.price), inStock: true }));
      const specs = Object.fromEntries(
        Object.entries(form.specs).filter(([, v]) => v?.trim()).map(([k, v]) => [k, v.trim()])
      );
      const payload = {
        catalogueId: catalogue.catalogueId,
        businessId:  business.businessId,
        name:        form.name.trim(),
        category:    form.category.trim(),
        description: form.description.trim(),
        price:       Number(form.price),
        itemType:    "product",
        photoUrl:    photoUrls[0] || null,
        photos:      photoUrls,
        variants, specs,
        tags:        form.tags.split(",").map(t => t.trim()).filter(Boolean),
        isActive:    true,
      };

      await fetch(`${API}/items${editItem ? "/" + editItem.itemId : ""}`, {
        method: editItem ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setShowForm(false);
      onRefresh();
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
      setProgress("");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? Cannot be undone.`)) return;
    setDeleting(item.itemId);
    try {
      await fetch(`${API}/items/${item.itemId}`, { method: "DELETE" });
      onRefresh();
    } catch { alert("Delete failed"); }
    finally { setDeleting(null); }
  };

  return (
    <div style={s.wrap}>
      <div style={s.headerRow}>
        <div style={s.title}>Designs ({designItems.length})</div>
        <button style={s.addBtn} onClick={openAdd}>+ Add Design</button>
      </div>

      {allCats.length > 2 && (
        <div style={s.pillsRow}>
          {allCats.map(c => (
            <button key={c}
              style={{ ...s.pill, ...(filterCat === c ? s.pillActive : {}) }}
              onClick={() => setFilterCat(c)}>{c}</button>
          ))}
        </div>
      )}

      <div style={s.grid}>
        {filtered.length === 0 && (
          <div style={s.empty}>No designs yet. Tap <b>+ Add Design</b> to upload your first one.</div>
        )}
        {filtered.map(item => (
          <div key={item.itemId} style={s.card}>
            <div style={s.cardPhotoWrap}>
              {(item.photoUrl || item.photos?.[0])
                ? <img src={item.photoUrl || item.photos[0]} alt={item.name} style={s.cardPhoto} />
                : <div style={s.cardNoPhoto}>📷</div>}
              {item.photos?.length > 1 && (
                <div style={s.photoBadge}>+{item.photos.length - 1}</div>
              )}
            </div>
            <div style={s.cardBody}>
              <div style={s.cardName}>{item.name}</div>
              <div style={s.cardPrice}>
                {item.variants?.length
                  ? `${inr(Math.min(...item.variants.map(v => v.price)))} onwards`
                  : inr(item.price)}
              </div>
              {item.category && <div style={s.cardCat}>{item.category}</div>}
              <div style={s.cardActions}>
                <button style={s.editBtn} onClick={() => openEdit(item)}>✏ Edit</button>
                <button style={s.deleteBtn} disabled={deleting === item.itemId}
                  onClick={() => handleDelete(item)}>
                  {deleting === item.itemId ? "..." : "🗑"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={s.overlay} onClick={() => !saving && setShowForm(false)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHeader}>
              <div style={s.drawerTitle}>{editItem ? "Edit Design" : "Add Design"}</div>
              <button style={s.closeBtn} onClick={() => !saving && setShowForm(false)}>✕</button>
            </div>
            <div style={s.drawerBody}>

              <div style={s.section}>
                <div style={s.label}>Photos (up to 4) *</div>
                <div style={s.photosRow}>
                  {form.photos.map((p, i) => (
                    <div key={i} style={s.photoThumb}>
                      <img src={p.preview || p.url} alt="" style={s.photoThumbImg} />
                      {i === 0 && <div style={s.mainBadge}>Main</div>}
                      <button style={s.photoRemove} onClick={() => removePhoto(i)}>✕</button>
                    </div>
                  ))}
                  {form.photos.length < 4 && (
                    <button style={s.photoAdd} onClick={() => photoInputRef.current?.click()}>
                      <div style={{ fontSize: 24 }}>📷</div>
                      <div style={{ fontSize: 11, marginTop: 4 }}>Add photo</div>
                    </button>
                  )}
                </div>
                <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                  multiple style={{ display: "none" }} onChange={handlePhotoSelect} />
              </div>

              <div style={s.section}>
                <div style={s.label}>Design name *</div>
                <input style={s.input} placeholder="e.g. Milano 3-Seater Sofa"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>

              <div style={s.section}>
                <div style={s.label}>Category</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  {catOptions.map(c => (
                    <button key={c}
                      style={{ ...s.catChip, ...(form.category === c ? s.catChipActive : {}) }}
                      onClick={() => setForm(f => ({ ...f, category: c }))}>{c}</button>
                  ))}
                </div>
                <input style={{ ...s.input, fontSize: 13 }} placeholder="Or type custom category..."
                  value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} />
              </div>

              <div style={s.section}>
                <div style={s.label}>Description</div>
                <textarea style={{ ...s.input, minHeight: 72, resize: "vertical" }}
                  placeholder="Brief description..." value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>

              <div style={s.section}>
                <div style={s.label}>Base price (₹) *</div>
                <input style={s.input} type="number" placeholder="e.g. 24999"
                  value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
              </div>

              <div style={s.section}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={s.label}>Variants (optional)</div>
                  <button style={s.addVariantBtn} onClick={addVariant}>+ Add</button>
                </div>
                {form.variants.map((v, i) => (
                  <div key={i} style={s.variantRow}>
                    <input style={{ ...s.input, flex: 2 }} placeholder="Label (e.g. Grey Fabric)"
                      value={v.label} onChange={e => updateVariant(i, "label", e.target.value)} />
                    <input style={{ ...s.input, flex: 1 }} type="number" placeholder="Price"
                      value={v.price} onChange={e => updateVariant(i, "price", e.target.value)} />
                    <button style={s.removeVariantBtn} onClick={() => removeVariant(i)}>✕</button>
                  </div>
                ))}
                {form.variants.length === 0 && (
                  <div style={{ fontSize: 12, color: "#999" }}>Add variants for different colours, sizes or materials.</div>
                )}
              </div>

              <div style={s.section}>
                <div style={s.label}>Specifications (optional)</div>
                {specFields.map(f => (
                  <div key={f.key} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{f.label}</div>
                    <input style={s.input} placeholder={f.placeholder}
                      value={form.specs[f.key] || ""}
                      onChange={e => updateSpec(f.key, e.target.value)} />
                  </div>
                ))}
              </div>

              <div style={s.section}>
                <div style={s.label}>Tags (comma separated)</div>
                <input style={s.input} placeholder="e.g. modern, wood, king size"
                  value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
              </div>

              {progress && <div style={s.progress}>{progress}</div>}

              <button style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
                onClick={handleSave} disabled={saving}>
                {saving ? (progress || "Saving...") : (editItem ? "Save Changes" : "Add Design")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { padding: "0 0 80px" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 12px" },
  title: { fontSize: 18, fontWeight: 700, color: "#111" },
  addBtn: { background: "#111", color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer" },
  pillsRow: { display: "flex", gap: 8, overflowX: "auto", padding: "0 16px 12px", scrollbarWidth: "none" },
  pill: { flexShrink: 0, padding: "5px 14px", borderRadius: 20, border: "1.5px solid #ddd", background: "transparent", color: "#666", fontSize: 13, cursor: "pointer" },
  pillActive: { background: "#111", borderColor: "#111", color: "#fff" },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, padding: "0 12px" },
  card: { background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  cardPhotoWrap: { position: "relative", paddingTop: "100%", background: "#f5f5f5", overflow: "hidden" },
  cardPhoto: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" },
  cardNoPhoto: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, color: "#ccc" },
  photoBadge: { position: "absolute", bottom: 6, left: 6, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 8 },
  cardBody: { padding: "10px 10px 12px" },
  cardName: { fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 3, lineHeight: 1.3 },
  cardPrice: { fontSize: 13, fontWeight: 700, color: "#b8722a", marginBottom: 4 },
  cardCat: { display: "inline-block", fontSize: 10, fontWeight: 600, color: "#888", background: "#f5f5f5", padding: "2px 7px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 8 },
  cardActions: { display: "flex", gap: 6 },
  editBtn: { flex: 1, padding: "6px 0", borderRadius: 6, border: "1px solid #ddd", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#333" },
  deleteBtn: { padding: "6px 10px", borderRadius: 6, border: "1px solid #fdd", background: "#fff8f8", fontSize: 12, cursor: "pointer", color: "#e03535" },
  empty: { gridColumn: "1 / -1", textAlign: "center", padding: "48px 20px", color: "#999", fontSize: 14, lineHeight: 1.6 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 500, display: "flex", alignItems: "flex-end" },
  drawer: { background: "#fff", width: "100%", maxWidth: 640, margin: "0 auto", borderRadius: "20px 20px 0 0", maxHeight: "92vh", display: "flex", flexDirection: "column" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 },
  drawerTitle: { fontSize: 17, fontWeight: 700, color: "#111" },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#999", padding: 4 },
  drawerBody: { overflowY: "auto", padding: "16px 20px 32px", flex: 1 },
  section: { marginBottom: 20 },
  label: { fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 8 },
  input: { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1.5px solid #e0e0e0", fontSize: 14, color: "#111", fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#fafafa" },
  photosRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  photoThumb: { position: "relative", width: 80, height: 80, borderRadius: 8, overflow: "hidden", flexShrink: 0, border: "1.5px solid #e0e0e0" },
  photoThumbImg: { width: "100%", height: "100%", objectFit: "cover" },
  mainBadge: { position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 9, fontWeight: 700, textAlign: "center", padding: "2px 0" },
  photoRemove: { position: "absolute", top: 3, right: 3, width: 18, height: 18, borderRadius: "50%", border: "none", background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  photoAdd: { width: 80, height: 80, borderRadius: 8, border: "1.5px dashed #ccc", background: "#fafafa", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#aaa", fontSize: 11, flexShrink: 0 },
  catChip: { padding: "4px 12px", borderRadius: 16, border: "1.5px solid #ddd", background: "transparent", fontSize: 12, cursor: "pointer", color: "#666" },
  catChipActive: { background: "#111", borderColor: "#111", color: "#fff" },
  variantRow: { display: "flex", gap: 8, marginBottom: 8, alignItems: "center" },
  addVariantBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#333" },
  removeVariantBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", color: "#ccc", padding: "0 4px", flexShrink: 0 },
  progress: { background: "#f0f7ff", border: "1px solid #cce0ff", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#1a6fc4", marginBottom: 12 },
  saveBtn: { width: "100%", padding: 14, borderRadius: 10, border: "none", background: "#111", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" },
  saveBtnDisabled: { background: "#999", cursor: "not-allowed" },
};
